import { useEffect, useRef } from 'react';

/**
 * Run `start` exactly once per component instance, on mount.
 *
 * This exists for one narrow but expensive case: an effect whose side effect is
 * **irreversible and outside the browser** — here, placing a real phone call.
 *
 * `useEffect(fn, [])` is widely, and wrongly, believed to mean "once". Empty
 * deps stop the effect re-running when props change; they do nothing about
 * React StrictMode, which deliberately mounts → unmounts → remounts in
 * development and so runs the body twice. For a fetch that only reads, the
 * duplicate is invisible. For a fetch that dials a telephone, it is a second
 * call to the patient.
 *
 * `CallPanel` previously tried to handle this by aborting the first request
 * from the effect's cleanup. That cannot work, and the reason is worth stating
 * plainly: **aborting a fetch only discards the browser's copy of the
 * response.** Whenever the request had already left the machine, the server
 * handled it and the phone rang — the abort merely hid the evidence from the
 * UI. It won the race often enough to look correct (~87% of dev calls) and
 * lost it the rest of the time. Prod interactions 961c6fe4, 02b3cddc and
 * 6e71cccf were each dialed twice this way; the second leg landed in the
 * patient's voicemail while they were talking to us on the first.
 *
 * A ref, not state: it must be set synchronously and survive the StrictMode
 * remount, which React guarantees for refs but not for a re-run effect body.
 *
 * SCOPE — this is a send-once guard, not a delivery guarantee. It stops US
 * sending a duplicate; it cannot stop a browser-level retry, a second tab, or
 * an impatient operator. The server-side claim
 * (`InteractionService.claimForDial`) is what actually makes "one dial per
 * interaction" true. Do not treat this hook as the protection.
 *
 * To start a genuinely new call, give the component a new `key` so React
 * builds a new instance with a fresh ref.
 */
export function useStartOnce(start: () => void): void {
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    start();
    // Deliberately empty: `start` is captured from the first mount, which is
    // the whole contract. Adding it to deps would re-arm on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
